import * as dom from "@/wc/registry/lib/dom/index.js";
import { analytics } from "@/lib/integrations/index.js";

interface Iguard {
    [key: string]: (pathname: string) => boolean;
}
const guards: Iguard = {};

interface INavigateOptions {
    // When replace is passed, it will replace the previous entry in the history
    // of the browser.
    replace?: boolean;
    // When an element is passed, it will determine the 'depth' of the passed
    // element and use that to determine what path should be routed to. Perfect
    // for components that are not written to be context-aware but still want
    // to provide context-aware routing. Check the documentation of
    // enrichElementPathnameWithContext for more context.
    element?: HTMLElement;

    // Sometimes we want to add history entries without other components
    // responding to it. For example when we are modifying url query parameters.
    silently?: boolean;
}

export function updateURLSearchParams(
    params: object,
    extraOptions?: INavigateOptions
) {
    const searchParams = new URLSearchParams(document.location.search);
    Object.entries(params).forEach(([key, value]) => {
        if (value !== "" && value !== undefined) {
            searchParams.set(key, value);
        } else {
            searchParams.delete(key);
        }
    });

    let url = document.location.pathname;
    if (searchParams.toString().length > 0) {
        url += "?" + searchParams.toString();
    }
    navigate(url, { silently: true, ...extraOptions });
}

// If allowed, update the url with the given pathname, and dispatch a routing
// event.
export async function navigate(
    pathname: string,
    options: INavigateOptions = {}
): Promise<boolean> {
    const replace = options.replace || false;
    const silently = options.silently || false;
    const element = options.element;

    // If element is passed, make sure that the pathname is only changed in the
    // allowed places. See the documentation of enrichPathnameWithElementContext
    // for more context.
    if (element) {
        pathname = await enrichPathnameWithElementContext(element, pathname);
    }

    // In case the pathname includes a relative path such as "..", check out
    // the current url and look up the "parent". This sounds a bit weird, but
    // should work similarly to how "cd" in unix is used.
    //
    // E.g. if the current url is stekz.com/foo/bar, and ".." is passed, return
    // stekz.com/foo.
    //
    // This makes it easier for generic components to navigate to the unknown
    // parent context.
    //
    // E.g. a specific process creation component might not know that it is used
    // in the context of /foo/, yet the component might know that it is currently
    // claiming a part of the url (/foo/some-task) and that it needs to return
    // to that unknown context once the process creation is completed.
    if (pathname.includes("..")) {
        const urlSegments = getURLSegments();
        const segments = pathname.split("/");
        for (let i = 0; i < segments.length; i++) {
            urlSegments.pop();
        }

        pathname = "/" + urlSegments.join("/");
    } else if (pathname.length > 2 && pathname.substring(0, 2) === "./") {
        pathname = `${document.location.pathname}/${pathname.substring(2)}`;
    }

    // Avoid navigating to the same place multiple times, unless the url query
    // parameters have changed.
    if (
        `${document.location.pathname}${document.location.search}` === pathname
    ) {
        return true;
    }

    // Keep the url query parameters if we haven't moved across pages. This
    // allows someone to share urls for pages that contain tables while also
    // sharing the exact filters they were using in that table.
    if (
        document.location.pathname === pathname &&
        document.location.search.length > 1
    ) {
        pathname += document.location.search;
    }

    let isPathAllowed = true;
    if (pathname) {
        for (const [key, isAllowed] of Object.entries(guards)) {
            if (!isAllowed(pathname)) {
                console.error(
                    `Navigation to ${pathname} is forbidden by ${key}.`
                );
                isPathAllowed = false;
                break;
            }
        }
    }
    if (!isPathAllowed) {
        return false;
    }

    // Replace can be passed when an item in the browser history does not need
    // to be remembered. E.g.
    // 1. User is on some-domain.com.
    // 2. User clicks on a 'abc' link, which navigates the user to
    //    some-domain.com/abc and renders the abc component. The abc component
    //    contains a router with a default route to /defg. This navigates
    //    the user to some-domain.com/abc/defg using replace=true.
    // 4. User clicks back, instead of being navigated to some-domain.com/abc
    //    (which would redirect the user to some-domain.com/abc/defg), the user
    //    is navigated to some-domain.com.
    if (replace) {
        window.history.replaceState({}, "", pathname);
    } else {
        window.history.pushState({}, "", pathname);
    }

    if (!silently) {
        routeEvents.dispatchEvent(
            new CustomEvent("navigate", { detail: { pathname } })
        );
    }

    // If there are any analytics integrations enabled, send the current
    // page.
    analytics.page();
    return true;
}

// Whenever someone clicks the 'back' button the last entry of the history
// is 'popped', it also triggers when a previous entry is being modified.
window.addEventListener("popstate", (e) => {
    // Sometimes we're using the location hash to emulate native-like
    // behavior. In that case we avoid emitting an event since 'hashchange'
    // will trigger the event when required.
    if ("window" in e.target && e.target.window.location.hash.includes("#to")) {
        return;
    }

    routeEvents.dispatchEvent(
        new CustomEvent("navigate", {
            detail: {
                pathname: document.location.pathname,
            },
        })
    );
});

window.addEventListener("hashchange", () => {
    navigateToUrlInHashIfRequired(window.location.hash);
});

async function navigateToUrlInHashIfRequired(hash: string) {
    if (hash.includes("#to=")) {
        const url = hash.replace("#to=", "");

        const isSuccess = await navigate(url, { replace: true });
        if (!isSuccess && window.location.hash.includes("#")) {
            window.history.replaceState({}, "", window.location.pathname);
        }
    }
}

// Converts a pathname to parts, and strips out empty fields.
// E.g. /customer/5/edit would give ['customer', '5', 'edit'].
export function getURLSegments(): string[] {
    return document.location.pathname.split("/").filter((part) => part !== "");
}

// Disclaimer: this is kind of an experimental function to make our lives
// easier, and allows us to e.g. share 'edit button components' on several
// depths within the DOM tree.
//
// Explanation:
//
// Given an element, look at its depth within the dom tree, decide what part
// of the url it is allowed to influence.
//
// E.g. the following situation:
//
// <c-router>
//   <c-dashboard>
//     <button id="A">navigate to producers</button>
//     <c-resource-router>
//       <c-customer-detail>
//         <button id="B">navigate to edit specific customer</button>
//       </c-customer-detail>
//     </c-resource-router>
//   </c-dashboard>
// </c-router>
//
//  When on the following page:
//  /dashboard/customer/5
//  ^                    ^
//  Button A             Button B
//
//  Button A has the power to change everything for the 1st 'depth', so if
//    button A calls this function with 'producers', it will return
//    "/producers".
//
//  Button B has the power to change everything for the 5th 'depth', so if
//    button B calls this function with 'edit', it will return
//    "/dashboard/customer/5/edit".
//
//  Basically, this function looks at the DOM structure, determines what
//  components are taking up what part of the pathname, and decides
//  what part it is allowed to replace.
export async function enrichPathnameWithElementContext(
    context: HTMLElement,
    pathname: string
) {
    // Get the url segment index of this component.
    const index = await dom.getUnclaimedURLSegmentsStartIndex(context);

    // Split up the pathname in parts.
    const urlSegments = getURLSegments();

    // Only remove the parts that we can miss. E.g. if on depth 4 and
    // the url is /customers/5/edit/info, we are allowed to splice 'info'.
    urlSegments.splice(index);

    // When there is nothing in the url, avoid adding an extra '/'.
    if (urlSegments.length === 0) {
        return "/" + pathname;
    }
    // Reconstruct an url given the pathname. E.g. if 'files' is given as a
    // pathname and the original url was /customers/5/edit/info with depth 4,
    // it will now be /customers/5/edit/files.
    return "/" + urlSegments.join("/") + "/" + pathname;
}

// Add a guard, which is a function that returns true or false depending on
// whether it is permitted to navigate to the given pathname.
export function registerGuard(
    guardHash: string,
    fn: (pathname: string) => boolean
) {
    guards[guardHash] = fn;
}

export function unRegisterGuard(guardHash: string) {
    if (guardHash in guards) {
        delete guards[guardHash];
    }
}

export const routeEvents = new EventTarget();

// In case this file (and thus website) just loaded, check if there is a hash
// present and navigate to what is referenced in the hash if required. This
// allows users to open up new pages through a href tags through native
// browser behavior.
navigateToUrlInHashIfRequired(window.location.hash);
